import React, { useState, useRef, useEffect, useMemo } from "react";
import type {
  Task,
  TaskTemplate,
  RoomStatus,
  CustomColumn,
  CustomColumnValue,
} from "../types/types.ts";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorBg,
  PaletteColorDarkBg,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { useTestMode } from "../hooks/useTestMode.ts";
import { triggerEffect } from "./effects.ts";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckIcon from "@mui/icons-material/Check";
import {
  isoToYmd,
  todayYmd,
  getDeadlineState,
  getDeadlineLabel,
  DEADLINE_COLORS,
} from "../utils/date.ts";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import TaskIcon from "@mui/icons-material/Task";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

interface ExtendedTaskTemplate extends TaskTemplate {
  templateType: "Single" | "Multi" | "Subtask" | string;
}

type TaskItemProps = {
  task: Task;
  isMobile: boolean;
  isSelected: boolean;
  selectedSubtaskId?: string;
  showSubtasksOnMobile?: boolean;
  onSelectDetail: (parent: Task, child?: Task) => void;
  onToggleComplete: (taskId: string, completed: boolean) => void;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  onToggleStar: (taskId: string, starred: boolean) => void;
  onToggleSubtask: (taskId: string, subtaskId: string) => void;
  refreshTodos?: () => void;
  onToggleNotification: (taskId: string, notification: boolean) => void;
  color: PaletteColor;
  roomId: number;
  honbuId: number;
  roomStatuses: RoomStatus[];
  reorderSubtasks?: (
    parentTaskId: string,
    subtaskIds: string[],
  ) => Promise<void>;
  onAddSubtask?: (parentTaskId: string, title: string) => Promise<string | null>;
  /** プロジェクトのカスタム列定義（TaskList表示設定用） */
  customColumns?: CustomColumn[];
  /** アクティブリストのカスタム列の値（全タスク分） */
  customColumnValues?: CustomColumnValue[];
  /** TaskItem に表示するカスタム列ID（表示順） */
  visibleCustomColumnIds?: number[];
};

const statusConfig: {
  [key in Task["status"]]: { color: string; circleColor: string };
} = {
  未対応: { color: "text-gray-500", circleColor: "bg-gray-400" },
  対応中: { color: "text-blue-500", circleColor: "bg-blue-500" },
  完了: { color: "text-green-500", circleColor: "bg-green-500" },
  待機: { color: "text-cyan-500", circleColor: "bg-cyan-500" },
  非表示: { color: "text-black", circleColor: "bg-black" },
};

// --- Confetti Particle ---
interface ConfettiParticleProps {
  id: number;
  style: React.CSSProperties;
}
const ConfettiParticle: React.FC<ConfettiParticleProps> = ({ id, style }) => (
  <div key={id} style={style}></div>
);

const PriorityBadge = ({ priority }: { priority: Task["priority"] }) => {
  const priorityConfig = {
    高: "bg-red-500 text-white",
    中: "bg-orange-500 text-white",
    低: "bg-blue-500 text-white",
    なし: "",
  };
  if (priority === "なし" || (priority as string) === "medium" || !priority)
    return null;
  return (
    <span
      className={`px-1.5 py-[2px] rounded-full text-[10px] leading-none font-semibold ${priorityConfig[priority]}`}
    >
      {priority}
    </span>
  );
};

export const StatusDropdown = ({
  task,
  onUpdate,
  isMobile,
  roomStatuses,
}: {
  task: Task;
  onUpdate: (id: string, status: Task["status"]) => void;
  isMobile: boolean;
  roomStatuses: RoomStatus[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const currentStatusInfo = roomStatuses.find(
    (s) => s.statusName === task.status,
  );

  const StatusDisplay = ({ status }: { status: Task["status"] }) => (
    <span
      className="font-semibold text-xs whitespace-nowrap"
      style={{
        color: currentStatusInfo
          ? PaletteColorHex[currentStatusInfo.color]
          : "#8A94A6",
      }}
    >
      {status}
    </span>
  );

  return (
    <div className="relative flex-shrink-0" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex items-center space-x-1 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-md h-6"
        style={{ lineHeight: "1" }}
      >
        <StatusDisplay status={task.status} />
        {!isMobile && (
          <ExpandMoreIcon
            fontSize="inherit"
            className={`w-3.5 h-3.5 text-gray-500 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {isOpen && (
        <div className="absolute z-30 top-full mt-1 bg-white shadow-lg rounded-md border w-32">
          {/* roomStatuses を元にドロップダウンの選択肢を動的に生成 */}
          {roomStatuses.map((statusInfo) => (
            <button
              key={statusInfo.roomStatusId}
              onClick={(e) => {
                e.stopPropagation();
                onUpdate(task.id, statusInfo.statusName as Task["status"]);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm flex items-center space-x-2 hover:bg-gray-100"
            >
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: PaletteColorHex[statusInfo.color] }}
              ></span>
              <span className="text-black whitespace-nowrap">
                {statusInfo.statusName}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  isMobile,
  isSelected,
  selectedSubtaskId,
  showSubtasksOnMobile = false,
  onSelectDetail,
  onToggleComplete,
  onUpdateStatus,
  onToggleStar,
  refreshTodos,
  onToggleNotification,
  color,
  roomId,
  honbuId,
  roomStatuses,
  reorderSubtasks,
  onAddSubtask,
  customColumns,
  customColumnValues,
  visibleCustomColumnIds,
}) => {
  // ダークモード状態を取得
  const isDarkMode = useDarkMode();
  // テストモード（添付ファイルバッジの表示切替）
  const isTestMode = useTestMode();
  // ダークモード対応の選択背景色
  const selectedBgColor = isDarkMode
    ? PaletteColorDarkBg[color]
    : PaletteColorBg[color];

  const [isExpanded, setIsExpanded] = useState(false);
  const [confetti, setConfetti] = useState<ConfettiParticleProps[]>([]);
  const checkboxRef = useRef<HTMLDivElement>(null);
  const subtaskCheckboxRef = useRef<HTMLDivElement>(null);
  const [subtaskTemplates, setSubtaskTemplates] = useState<
    ExtendedTaskTemplate[]
  >([]);
  const [showSubtaskTemplates, setShowSubtaskTemplates] = useState(false);
  const templateMenuRef = useRef<HTMLDivElement>(null);
  const templateButtonRef = useRef<HTMLButtonElement>(null);
  const [templatePosition, setTemplatePosition] = useState<"top" | "bottom">(
    "top",
  );

  useEffect(() => {
    if (showSubtaskTemplates && templateButtonRef.current) {
      const rect = templateButtonRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow > 240 || spaceBelow > spaceAbove) {
        setTemplatePosition("bottom");
      } else {
        setTemplatePosition("top");
      }
    }
  }, [showSubtaskTemplates]);

  useEffect(() => {
    if (!isSelected) {
      setIsExpanded(false);
    }
  }, [isSelected]);

  // サブタスクが選択されたら一覧を展開
  useEffect(() => {
    if (selectedSubtaskId && isSelected) {
      setIsExpanded(true);
    }
  }, [selectedSubtaskId, isSelected]);

  useEffect(() => {
    if (isExpanded) {
      const fetchSubtaskTemplates = async () => {
        try {
          const res = await fetch(
            `/api/templates?roomId=${roomId}&honbuId=${honbuId}`,
          );
          if (res.ok) {
            const data = await res.json();
            const templatesWithType = data.map((template: any) => {
              const taskData = JSON.parse(template.taskData || "{}");
              const childTasks = normalizeTemplateChildren(taskData.childTasks);
              return {
                ...template,
                templateType: childTasks.length > 0 ? "Multi" : "Single",
              };
            });
            setSubtaskTemplates(templatesWithType as ExtendedTaskTemplate[]);
          }
        } catch (error) {
          console.error("Failed to fetch subtask templates:", error);
        }
      };
      fetchSubtaskTemplates();
    }
  }, [isExpanded, roomId, honbuId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        templateMenuRef.current &&
        !templateMenuRef.current.contains(event.target as Node)
      ) {
        setShowSubtaskTemplates(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDateRange = (start?: string | null, end?: string | null) => {
    const s = isoToYmd(start || "");
    if (!s) return "ー";
    const e = isoToYmd(end || "");
    return e && e !== s ? `${s} ~ ${e}` : s;
  };

  const today = todayYmd();

  // 期限切れ（赤）・今日期限（オレンジ）の行に付ける左ボーダー。該当しなければ undefined
  const deadlineBorderColor = (t: Task): string | undefined => {
    const state = getDeadlineState(t, today);
    return state === "none" ? undefined : DEADLINE_COLORS[state];
  };

  // 「期限: 2026-10-01 ⚠3日超過」の表示。期限切れは赤、今日期限はオレンジで強調する
  const renderDeadline = (t: Task) => {
    const state = getDeadlineState(t, today);
    const label = getDeadlineLabel(t, today);
    const accent = state === "none" ? undefined : DEADLINE_COLORS[state];
    return (
      <div className="flex items-center space-x-1 min-w-0 leading-none">
        <span className="font-bold text-gray-500 flex-shrink-0">期限:</span>
        <span
          className={`truncate ${accent ? "font-semibold" : ""}`}
          style={accent ? { color: accent } : undefined}
        >
          {formatDateRange(t.startDate, t.endDate)}
        </span>
        {label && (
          <span
            className="flex-shrink-0 flex items-center gap-0.5 font-bold"
            style={{ color: accent }}
          >
            {state === "overdue" && (
              <WarningAmberRoundedIcon sx={{ fontSize: 13 }} />
            )}
            {label}
          </span>
        )}
      </div>
    );
  };

  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );
  React.useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isWideScreen = viewportWidth >= 1300;
  const isNarrowScreen = !isMobile && !isWideScreen;

  const handleToggleComplete = (
    taskId: string,
    completed: boolean,
    isSubtask: boolean,
  ) => {
    onToggleComplete(taskId, completed);
    if (completed) {
      const completedStatus = roomStatuses.find((s) => s.statusName === "完了");
      if (completedStatus?.action && completedStatus.action !== "None") {
        const ref = isSubtask ? subtaskCheckboxRef : checkboxRef;
        if (ref.current) {
          triggerEffect(completedStatus.action, ref.current);
        }
      }
    }
  };

  const handleTaskClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectDetail(task);
    if (isWideScreen) setIsExpanded((v) => !v);
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded((v) => !v);
  };

  const handleSubtaskClick = (e: React.MouseEvent, child: Task) => {
    e.stopPropagation();
    onSelectDetail(task, child);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !reorderSubtasks) return;

    const items = Array.from(task.childTasks || []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const subtaskIds = items.map((item) => item.id);
    reorderSubtasks(task.id, subtaskIds);
  };

  const completedSubtasks =
    task.childTasks?.filter((t) => t.completed).length || 0;
  const totalSubtasks = task.childTasks?.length || 0;

  const hasFirstRowContent =
    (task.priority && !["なし", "medium"].includes(task.priority)) ||
    (task.tags && task.tags.length > 0);

  const starButton = (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggleStar(task.id, !task.starred);
      }}
      className={`p-1 rounded-full hover:bg-yellow-100 flex-shrink-0 h-6 w-6 flex items-center justify-center ${
        isNarrowScreen ? "mr-2" : ""
      }`}
      aria-label="スターを切り替え"
    >
      {task.starred ? (
        <StarIcon className="w-5 h-5 text-yellow-400" fontSize="medium" />
      ) : (
        <StarBorderIcon className="w-5 h-5 text-gray-300" fontSize="medium" />
      )}
    </button>
  );

  const normalizeTemplateChildren = (rawChildren: any): any[] => {
    if (!rawChildren) return [];
    if (Array.isArray(rawChildren)) return rawChildren;
    if (Array.isArray(rawChildren.$values)) return rawChildren.$values;
    return [];
  };

  const parseTemplateTaskData = (template: TaskTemplate) => {
    try {
      return JSON.parse(template.taskData || "{}");
    } catch {
      return {};
    }
  };

  const handleApplySubtaskTemplate = async (template: TaskTemplate) => {
    const templateTask = parseTemplateTaskData(template);
    const childTemplates = normalizeTemplateChildren(templateTask.childTasks);
    const templateType = (
      (template as ExtendedTaskTemplate).templateType || ""
    ).toLowerCase();
    const isMultiTemplate =
      ["multi", "subtask"].includes(templateType) || childTemplates.length > 0;

    const tasksToCreate = isMultiTemplate ? childTemplates : [templateTask];
    if (tasksToCreate.length === 0) {
      setShowSubtaskTemplates(false);
      return;
    }

    try {
      for (const subtaskTemplate of tasksToCreate) {
        const payload = {
          title:
            subtaskTemplate?.title ||
            templateTask?.title ||
            "テンプレートから作成",
        };

        const response = await fetch(`/api/tasks/${task.id}/subtasks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error("Failed to create subtask");
        }
      }

      if (refreshTodos) {
        await refreshTodos();
      }
    } catch (error) {
      console.error("Failed to create subtasks from template:", error);
      alert("サブタスクの作成に失敗しました。");
    } finally {
      setShowSubtaskTemplates(false);
    }
  };

  const bellButton = (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggleNotification(task.id, !task.notification);
      }}
      className="p-1 rounded-full hover:bg-gray-100 flex-shrink-0 h-6 w-6 flex items-center justify-center"
      aria-label="通知を切り替え"
    >
      {task.notification ? (
        <NotificationsIcon
          className="w-5 h-5 text-blue-500"
          fontSize="medium"
        />
      ) : (
        <NotificationsOffIcon
          className="w-5 h-5 text-gray-300"
          fontSize="medium"
        />
      )}
    </button>
  );

  // メインタスクのステータス色を取得
  const mainTaskStatusInfo = roomStatuses.find(
    (s) => s.statusName === task.status,
  );
  const mainTaskTitleColor = mainTaskStatusInfo
    ? mainTaskStatusInfo.color === "Gray"
      ? isDarkMode ? "#f3f4f6" : "#1f2937"
      : PaletteColorHex[mainTaskStatusInfo.color]
    : isDarkMode ? "#f3f4f6" : "#1f2937"; // デフォルト: text-gray-800 相当

  // TaskList表示設定で選択されたカスタム列を、表示順に値と組み合わせる
  const visibleCustomItems = useMemo(() => {
    if (
      !visibleCustomColumnIds ||
      visibleCustomColumnIds.length === 0 ||
      !customColumns ||
      customColumns.length === 0
    ) {
      return [] as { id: number; name: string; value: string }[];
    }
    const colById = new Map(customColumns.map((c) => [c.customColumnId, c]));
    const taskIdNum = Number(task.id);
    return visibleCustomColumnIds
      .map((id) => {
        const col = colById.get(id);
        if (!col) return null;
        const entry = (customColumnValues || []).find(
          (v) => v.taskId === taskIdNum && v.customColumnId === id,
        );
        return {
          id,
          name: col.columnName,
          value: entry?.value || "",
        };
      })
      .filter((v): v is { id: number; name: string; value: string } => !!v);
  }, [visibleCustomColumnIds, customColumns, customColumnValues, task.id]);

  // 1行に最大2項目ずつ表示するためペアに分割する
  const customItemRows = useMemo(() => {
    const rows: { id: number; name: string; value: string }[][] = [];
    for (let i = 0; i < visibleCustomItems.length; i += 2) {
      rows.push(visibleCustomItems.slice(i, i + 2));
    }
    return rows;
  }, [visibleCustomItems]);

  const customColumnsBlock = !task.completed && customItemRows.length > 0 && (
    <div className="mt-1 space-y-1 text-xs text-gray-600">
      {customItemRows.map((row, rowIndex) => (
        <div key={rowIndex} className="flex items-center justify-start gap-4">
          {row.map((item) => (
            <div
              key={item.id}
              className="flex items-center space-x-1 min-w-0 leading-none"
            >
              <span className="font-bold text-gray-500 flex-shrink-0 truncate max-w-[8rem]">
                {item.name}:
              </span>
              <span className="truncate">{item.value || "ー"}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const mainContent = (
    <div className="flex-grow min-w-0">
      {hasFirstRowContent && (
        <div className="mb-0.5 flex items-center justify-between leading-none">
          <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 flex-1 min-w-0">
            <PriorityBadge priority={task.priority} />
            {(task.tags || []).map((tag) => (
              <span
                key={tag.id}
                style={{
                  color: tag.color,
                  backgroundColor: `${tag.color}20`,
                  lineHeight: "1",
                }}
                className="font-semibold text-[10px] px-1.5 py-[2px] rounded-full"
              >
                #{tag.name}
              </span>
            ))}
          </div>
          <div className="ml-1 flex-shrink-0 flex items-center">
            {bellButton}
            {starButton}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-grow min-w-0">
          {totalSubtasks > 0 ? (
            <div className="flex items-center justify-center w-6 h-6 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-sm flex-shrink-0 leading-none">
              {completedSubtasks}/{totalSubtasks}
            </div>
          ) : (
            <div
              className="relative flex-shrink-0 w-6 h-6 flex items-center justify-center"
              ref={checkboxRef}
            >
              <input
                id={`task-checkbox-${task.id}`}
                type="checkbox"
                checked={task.completed}
                onChange={(e) =>
                  handleToggleComplete(task.id, e.target.checked, false)
                }
                onClick={(e) => e.stopPropagation()}
                className="peer appearance-none box-border w-5 h-5 border-2 border-gray-400 rounded-sm bg-white
                           checked:bg-green-500 checked:border-green-500 align-middle"
              />
              <CheckIcon
                fontSize="inherit"
                className="w-4 h-4 absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                }}
              />
            </div>
          )}
          <StatusDropdown
            task={task}
            onUpdate={onUpdateStatus}
            isMobile={isMobile}
            roomStatuses={roomStatuses}
          />
          <div className="flex items-center space-x-2 flex-grow min-w-0">
            <h3
              className={`font-semibold line-clamp-2 leading-snug ${
                task.completed ? "line-through" : ""
              }`}
              style={{
                color: mainTaskTitleColor,
              }}
            >
              {task.title}
            </h3>
            {/* F017: 添付ファイルバッジ（テストモード時のみ表示） */}
            {isTestMode && (task.taskFiles?.length ?? 0) > 0 && (
              <span
                className="flex items-center text-gray-400 text-[11px] flex-shrink-0 leading-none"
                title={`添付ファイル ${task.taskFiles!.length}件`}
              >
                <AttachFileIcon sx={{ fontSize: 14 }} />
                {task.taskFiles!.length}
              </span>
            )}
          </div>
        </div>
        {!hasFirstRowContent && (
          <div className="flex-shrink-0 flex items-center">
            {bellButton}
            {starButton}
          </div>
        )}
      </div>
      <div className="flex items-center justify-start mt-1 text-xs text-gray-600 gap-4">
        {task.completed && task.completedAt ? (
          <>
            {/* 完了済タスクの表示 */}
            <div className="flex items-center space-x-1 min-w-0 leading-none">
              <span className="font-bold text-gray-500 flex-shrink-0">
                完了者:
              </span>
              <span className="truncate">{task.completedBy?.name || "ー"}</span>
            </div>
            <div className="flex items-center space-x-1 min-w-0 leading-none">
              <span className="font-bold text-gray-500 flex-shrink-0">
                完了日:
              </span>
              <span className="truncate">{isoToYmd(task.completedAt)}</span>
            </div>
          </>
        ) : (
          <>
            {/* 未完了タスクの表示 */}
            {roomId !== 0 && (
              <div className="flex items-center space-x-1 min-w-0 leading-none">
                <span className="font-bold text-gray-500 flex-shrink-0">
                  担当:
                </span>
                <span className="truncate">
                  {(task.assignees || []).map((a) => a.name).join(", ") || "ー"}
                </span>
              </div>
            )}
            {renderDeadline(task)}
          </>
        )}
      </div>
      {customColumnsBlock}
    </div>
  );

  const desktopSideInfo = (
    <>
      <div className="flex-shrink-0 w-auto text-xs text-gray-600 space-y-1"></div>
      <div className="flex-shrink-0 w-8 flex flex-col items-center space-y-2">
        {isNarrowScreen && (
          <button
            onClick={handleToggleExpand}
            className="text-gray-400 hover:text-gray-600"
          >
            <ExpandMoreIcon
              className={`w-5 h-5 transition-transform ${
                isExpanded ? "rotate-180" : "-rotate-90"
              }`}
              fontSize="inherit"
            />
          </button>
        )}
      </div>
    </>
  );

  return (
    <div
      className={`bg-white rounded-lg border hover:shadow-md transition-all duration-200 flex flex-col ${
        isSelected && !isMobile ? "shadow-lg" : "border-gray-200"
      }`}
      style={{
        ...(isSelected && !isMobile
          ? { borderColor: PaletteColorHex[color] }
          : {}),
        ...(deadlineBorderColor(task)
          ? { borderLeft: `4px solid ${deadlineBorderColor(task)}` }
          : {}),
      }}
    >
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-50">
        {confetti.map((c) => (
          <ConfettiParticle key={c.id} {...c} />
        ))}
      </div>

      <div
        className={`relative p-3 flex gap-3 cursor-pointer ${
          isNarrowScreen ? "pr-14" : ""
        }`}
        style={
          isSelected && !selectedSubtaskId && !isMobile
            ? { backgroundColor: selectedBgColor }
            : {}
        }
        onClick={handleTaskClick}
      >
        {mainContent}

        {isNarrowScreen && (
          <button
            onClick={handleToggleExpand}
            aria-label="サブタスクの表示切り替え"
            title="サブタスクの表示切り替え"
            className={`absolute right-0 top-0 h-full w-14 z-10 border-l flex items-center justify-center ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 border-gray-600"
                : "bg-blue-50 hover:bg-blue-100 border-blue-100"
            }`}
          >
            <ExpandMoreIcon
              className={`w-5 h-5 transition-transform duration-200 ${
                isDarkMode ? "text-gray-300" : "text-blue-700"
              } ${isExpanded ? "rotate-180" : "-rotate-90"}`}
            />
          </button>
        )}
      </div>
      {((isExpanded && !isMobile) || (isMobile && showSubtasksOnMobile)) &&
        !task.completed && (
          <div
            className={`border-t ${
              isDarkMode ? "bg-gray-800/50" : "bg-gray-50/50"
            }`}
          >
            {/* スマホではドラッグ機能を無効化 */}
            {!isMobile ? (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={`subtasks-${task.id}`}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {(task.childTasks || []).map((child, index) => (
                        <Draggable
                          key={child.id}
                          draggableId={child.id}
                          index={index}
                          isDragDisabled={isMobile}
                        >
                          {(provided, snapshot) => {
                            // サブタスクのステータス色を取得
                            const subtaskStatusInfo = roomStatuses.find(
                              (s) => s.statusName === child.status,
                            );
                            const subtaskTitleColor = subtaskStatusInfo
                              ? subtaskStatusInfo.color === "Gray"
                                ? isDarkMode ? "#e5e7eb" : "#374151"
                                : PaletteColorHex[subtaskStatusInfo.color]
                              : isDarkMode ? "#e5e7eb" : "#374151"; // デフォルト: text-gray-700 相当

                            return (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                onClick={(e) => handleSubtaskClick(e, child)}
                                className={`w-full pl-4 pr-3 py-2 border-b last:border-b-0 hover:bg-opacity-50 ${
                                  snapshot.isDragging
                                    ? "bg-blue-100 shadow-lg"
                                    : ""
                                }`}
                                style={{
                                  ...provided.draggableProps.style,
                                  ...(selectedSubtaskId === child.id &&
                                  !isMobile
                                    ? { backgroundColor: selectedBgColor }
                                    : {}),
                                  ...(deadlineBorderColor(child)
                                    ? {
                                        borderLeft: `3px solid ${deadlineBorderColor(child)}`,
                                      }
                                    : {}),
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  {/* ドラッグハンドル（スマホ版では非表示） */}
                                  {!isMobile && (
                                    <div
                                      {...provided.dragHandleProps}
                                      className="flex-shrink-0 cursor-grab active:cursor-grabbing"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <DragIndicatorIcon className="text-gray-400 w-5 h-5" />
                                    </div>
                                  )}

                                  <div className="flex items-center gap-3 flex-grow min-w-0">
                                    <div
                                      className="relative flex-shrink-0 w-6 h-6 flex items-center justify-center"
                                      ref={subtaskCheckboxRef}
                                    >
                                      <input
                                        id={`task-checkbox-${child.id}`}
                                        type="checkbox"
                                        checked={child.completed}
                                        onChange={(e) =>
                                          handleToggleComplete(
                                            child.id,
                                            e.target.checked,
                                            true,
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        className="peer appearance-none box-border w-5 h-5 border-2 border-gray-400 rounded-sm bg-white
                                 checked:bg-green-500 checked:border-green-500 align-middle"
                                      />
                                      <CheckIcon
                                        fontSize="inherit"
                                        className="w-4 h-4 absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none"
                                        style={{
                                          top: "50%",
                                          left: "50%",
                                          transform: "translate(-50%,-50%)",
                                        }}
                                      />
                                    </div>
                                    <StatusDropdown
                                      task={child}
                                      onUpdate={onUpdateStatus}
                                      isMobile={isMobile}
                                      roomStatuses={roomStatuses}
                                    />
                                    <h4
                                      className={`font-medium truncate leading-snug ${
                                        child.completed
                                          ? "line-through text-gray-500"
                                          : ""
                                      }`}
                                      style={{
                                        color: child.completed
                                          ? undefined
                                          : subtaskTitleColor,
                                      }}
                                    >
                                      {child.title}
                                    </h4>
                                  </div>
                                  <div className="flex-shrink-0 w-8 flex flex-col items-center" />
                                </div>
                                <div className="flex items-center justify-start mt-1 ml-8 text-xs text-gray-600 gap-4">
                                  {child.completed && child.completedAt ? (
                                    <>
                                      {/* 完了済サブタスクの表示 */}
                                      <div className="flex items-center space-x-1 min-w-0 leading-none">
                                        <span className="font-bold text-gray-500 flex-shrink-0">
                                          完了者:
                                        </span>
                                        <span className="truncate">
                                          {child.completedBy?.name || "ー"}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-1 min-w-0 leading-none">
                                        <span className="font-bold text-gray-500 flex-shrink-0">
                                          完了日:
                                        </span>
                                        <span className="truncate">
                                          {isoToYmd(child.completedAt)}
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      {/* 未完了サブタスクの表示 */}
                                      {roomId !== 0 && (
                                        <div className="flex items-center space-x-1 min-w-0 leading-none">
                                          <span className="font-bold text-gray-500 flex-shrink-0">
                                            担当:
                                          </span>
                                          <span className="truncate">
                                            {(child.assignees || [])
                                              .map((a) => a.name)
                                              .join(", ") || "ー"}
                                          </span>
                                        </div>
                                      )}
                                      {renderDeadline(child)}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              // スマホ用：ドラッグ機能なし
              <div>
                {(task.childTasks || []).map((child, index) => {
                  // サブタスクのステータス色を取得
                  const subtaskStatusInfo = roomStatuses.find(
                    (s) => s.statusName === child.status,
                  );
                  const subtaskTitleColor = subtaskStatusInfo
                    ? subtaskStatusInfo.color === "Gray"
                      ? isDarkMode ? "#e5e7eb" : "#374151"
                      : PaletteColorHex[subtaskStatusInfo.color]
                    : isDarkMode ? "#e5e7eb" : "#374151";

                  return (
                    <div
                      key={child.id}
                      onClick={(e) => handleSubtaskClick(e, child)}
                      className={`w-full pl-4 pr-3 py-2 border-b last:border-b-0 hover:bg-opacity-50`}
                      style={{
                        ...(selectedSubtaskId === child.id && !isMobile
                          ? { backgroundColor: selectedBgColor }
                          : {}),
                        ...(deadlineBorderColor(child)
                          ? {
                              borderLeft: `3px solid ${deadlineBorderColor(child)}`,
                            }
                          : {}),
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-3 flex-grow min-w-0">
                          <div
                            className="relative flex-shrink-0 w-6 h-6 flex items-center justify-center"
                            ref={subtaskCheckboxRef}
                          >
                            <input
                              id={`task-checkbox-${child.id}`}
                              type="checkbox"
                              checked={child.completed}
                              onChange={(e) =>
                                handleToggleComplete(
                                  child.id,
                                  e.target.checked,
                                  true,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="peer appearance-none box-border w-5 h-5 border-2 border-gray-400 rounded-sm bg-white
                                 checked:bg-green-500 checked:border-green-500 align-middle"
                            />
                            <CheckIcon
                              fontSize="inherit"
                              className="w-4 h-4 absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none"
                              style={{
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%,-50%)",
                              }}
                            />
                          </div>
                          <StatusDropdown
                            task={child}
                            onUpdate={onUpdateStatus}
                            isMobile={isMobile}
                            roomStatuses={roomStatuses}
                          />
                          <h4
                            className={`font-medium truncate leading-snug ${
                              child.completed
                                ? "line-through text-gray-500"
                                : ""
                            }`}
                            style={{
                              color: child.completed
                                ? undefined
                                : subtaskTitleColor,
                            }}
                          >
                            {child.title}
                          </h4>
                        </div>
                        <div className="flex-shrink-0 w-8 flex flex-col items-center" />
                      </div>
                      <div className="flex items-center justify-start mt-1 ml-8 text-xs text-gray-600 gap-4">
                        {child.completed && child.completedAt ? (
                          <>
                            <div className="flex items-center space-x-1 min-w-0 leading-none">
                              <span className="font-bold text-gray-500 flex-shrink-0">
                                完了者:
                              </span>
                              <span className="truncate">
                                {child.completedBy?.name || "ー"}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1 min-w-0 leading-none">
                              <span className="font-bold text-gray-500 flex-shrink-0">
                                完了日:
                              </span>
                              <span className="truncate">
                                {isoToYmd(child.completedAt)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            {roomId !== 0 && (
                              <div className="flex items-center space-x-1 min-w-0 leading-none">
                                <span className="font-bold text-gray-500 flex-shrink-0">
                                  担当:
                                </span>
                                <span className="truncate">
                                  {(child.assignees || [])
                                    .map((a) => a.name)
                                    .join(", ") || "ー"}
                                </span>
                              </div>
                            )}
                            {renderDeadline(child)}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {(isSelected ||
              isNarrowScreen ||
              (isMobile && showSubtasksOnMobile)) && (
              <div className="w-full pl-8 pr-3 py-2 flex items-center gap-4 bg-white border-t">
                <button
                  className="hover:underline bg-transparent px-0 py-0 text-xs font-semibold rounded-none shadow-none flex items-center gap-1"
                  style={{
                    background: "none",
                    color: PaletteColorHex[color],
                  }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (onAddSubtask) {
                      await onAddSubtask(task.id, "新規サブタスク");
                    } else {
                      await fetch(`/api/tasks/${task.id}/subtasks`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ title: "新規サブタスク" }),
                      });
                      if (refreshTodos) {
                        await refreshTodos();
                      }
                    }
                  }}
                >
                  <AddIcon fontSize="inherit" />
                  サブタスクを追加
                </button>

                <div className="relative" ref={templateMenuRef}>
                  <button
                    ref={templateButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSubtaskTemplates(!showSubtaskTemplates);
                    }}
                    className="hover:underline bg-transparent px-0 py-0 text-xs font-semibold rounded-none shadow-none flex items-center gap-1"
                    style={{
                      background: "none",
                      color: PaletteColorHex[color],
                    }}
                  >
                    <AddIcon fontSize="inherit" />
                    テンプレートを使用
                  </button>
                  {showSubtaskTemplates && (
                    <div className="absolute bottom-full mb-2 w-64 bg-white border rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                      {subtaskTemplates.length > 0 ? (
                        subtaskTemplates.map((template) => (
                          <button
                            key={template.templateId}
                            onClick={() => handleApplySubtaskTemplate(template)}
                            className="w-full text-left p-3 text-sm hover:bg-gray-100 flex flex-col gap-1"
                          >
                            <div className="flex items-center gap-2">
                              {/* テンプレートタイプに応じたアイコン */}
                              {template.templateType === "Multi" ? (
                                <ListAltIcon
                                  fontSize="small"
                                  titleAccess="マルチテンプレート"
                                />
                              ) : (
                                <TaskIcon
                                  fontSize="small"
                                  titleAccess="シングルテンプレート"
                                />
                              )}
                              <span className="truncate">
                                {template.templateName}
                              </span>
                              {/* 共有中の表示 */}
                              {template.isShare && (
                                <span className="px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                                  共有
                                </span>
                              )}
                            </div>
                            {/* サブタスク数の表示 */}
                            {template.templateType === "Multi" && (
                              <p className="text-xs text-gray-500">
                                {JSON.parse(template.taskData).childTasks
                                  ?.length || 0}
                                件のサブタスクが作られます
                              </p>
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="p-3 text-sm text-gray-500">
                          利用可能なサブタスクのテンプレートはありません。
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
};
